'use client';

import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink } from 'lucide-react';

interface StudioAdBannerProps {
  variant: 'pc' | 'mobile';
  className?: string;
}

export function StudioAdBanner({ variant, className = '' }: StudioAdBannerProps) {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const el = containerRef.current;
    if (!el) return;

    // Use IntersectionObserver to lazy load the banner only when user scrolls near it (250px margin)
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true);
              observer.disconnect();
            }
          });
        },
        { rootMargin: '250px 0px', threshold: 0.01 }
      );

      observer.observe(el);
      return () => observer.disconnect();
    } else {
      setIsInView(true);
    }
  }, []);

  const isPc = variant === 'pc';
  const targetUrl = 'https://studio.bop.ink';
  const s3Url = isPc
    ? 'https://s3.cloudfly.vn/adsbopink/bopstudio-970.png'
    : 'https://s3.cloudfly.vn/adsbopink/bopstudio-300.png';
  const webpUrl = isPc
    ? '/ads/bopstudio-970.webp'
    : '/ads/bopstudio-300.webp';

  // Aspect ratio styling:
  // PC: 4117 x 382 (~10.78 : 1)
  // Mobile: 1374 x 1145 (~1.2 : 1)
  const aspectClass = isPc ? 'aspect-[4117/382]' : 'aspect-[1374/1145]';
  const maxDimensions = isPc ? 'w-full max-w-full' : 'w-full max-w-[340px] mx-auto';

  return (
    <div
      ref={containerRef}
      className={`relative rounded-2xl overflow-hidden border border-slate-200/80 bg-slate-50 transition-all ${aspectClass} ${maxDimensions} ${className}`}
      aria-label="Quảng cáo tài trợ - BOP Studio"
    >
      {/* Sponsor Tag */}
      <span className="absolute top-2 right-2 z-10 px-1.5 py-0.5 text-[9px] font-semibold tracking-wider uppercase rounded-md bg-slate-900/60 text-white backdrop-blur-xs pointer-events-none">
        Tài trợ
      </span>

      {isInView ? (
        <a
          href={targetUrl}
          target="_blank"
          rel="noopener sponsored"
          className="group block w-full h-full relative overflow-hidden"
          title="Khám phá BOP Studio - Nền tảng sáng tạo & biên tập trực tuyến (studio.bop.ink)"
        >
          <picture>
            <source srcSet={webpUrl} type="image/webp" />
            <img
              src={s3Url}
              alt="BOP Studio - Nền tảng biên tập & sáng tạo chuyên nghiệp"
              loading="lazy"
              decoding="async"
              onLoad={() => setIsLoaded(true)}
              className={`w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-95 ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            />
          </picture>

          {/* Hover overlay hint */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-end p-2 pointer-events-none">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-slate-900 text-[10px] font-bold px-2 py-1 rounded-lg shadow-xs flex items-center gap-1">
              <span>Mở studio.bop.ink</span>
              <ExternalLink className="w-3 h-3" />
            </span>
          </div>
        </a>
      ) : (
        /* Lightweight placeholder before scroll: 0 network requests, maintains strict layout height to prevent CLS */
        <div className="w-full h-full flex flex-col items-center justify-center p-3 text-slate-400 select-none">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400">
            <span>BOP Studio</span>
            <ExternalLink className="w-3 h-3 opacity-60" />
          </div>
          <span className="text-[10px] text-slate-400/80 mt-0.5">studio.bop.ink</span>
        </div>
      )}
    </div>
  );
}

export default StudioAdBanner;
